import { useEffect, useState } from "react";
import api from "@/lib/api";
import { MaskedLine, Reveal } from "@/components/Reveal";
import { useLanguage } from "@/lib/i18n";
import { useSeo } from "@/lib/seo";
import { formatNoteDate } from "@/lib/dates";
import { thumbUrl } from "@/lib/images";

// Stabile Streuung pro Notiz: jede Notiz bekommt ihre eigene Bildanordnung,
// die beim erneuten Laden gleich bleibt (abgeleitet aus der Notiz-ID)
const hashCode = (s) => [...s].reduce((h, c) => (h * 31 + c.charCodeAt(0)) | 0, 7);

const scatterFor = (id) => {
  let h = hashCode(id);
  const next = () => {
    h = (h * 1103515245 + 12345) & 0x7fffffff;
    return h / 0x7fffffff;
  };
  return [0, 1, 2].map(() => ({
    rotate: +(next() * 8 - 4).toFixed(1), // -4° bis +4°
    shiftY: Math.round(next() * 56 - 20), // -20 bis +36 px vertikal
    overlap: Math.round(24 + next() * 40), // 24–64 px Überlappung
  }));
};

export default function Journal() {
  const [notes, setNotes] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(false);
  const [applauded, setApplauded] = useState(
    () => new Set(JSON.parse(localStorage.getItem("wj-applauded") || "[]"))
  );
  const { lang, t } = useLanguage();
  // Streuung der Abzüge nur auf Desktop — auf Mobil bleiben die Bilder ruhig und größer
  const [isDesktop, setIsDesktop] = useState(
    () => typeof window !== "undefined" && window.matchMedia("(min-width: 768px)").matches
  );
  useEffect(() => {
    const mq = window.matchMedia("(min-width: 768px)");
    const onChange = (e) => setIsDesktop(e.matches);
    mq.addEventListener("change", onChange);
    return () => mq.removeEventListener("change", onChange);
  }, []);
  useSeo({
    title: "Fieldnotes · Wim Jansen Delgado · Fotograf Hamburg",
    description:
      "Die Fieldnotes von Wim Jansen Delgado: kurze Notizen und Bilder aus Küchen, Hotels und von Reisen.",
    path: "/fieldnotes",
  });

  // Ein erneuter Versuch nach 1,5 s, danach ruhige Fehlermeldung mit Retry
  const load = (attempt = 0) => {
    setLoading(true);
    api
      .get("/fieldnotes")
      .then((res) => {
        setNotes(res.data);
        setError(false);
        setLoading(false);
      })
      .catch(() => {
        if (attempt < 1) {
          setTimeout(() => load(attempt + 1), 1500);
        } else {
          setNotes([]);
          setError(true);
          setLoading(false);
        }
      });
  };

  useEffect(() => {
    load();
  }, []);

  const applaud = async (noteId) => {
    if (applauded.has(noteId)) return;
    const next = new Set(applauded).add(noteId);
    setApplauded(next);
    localStorage.setItem("wj-applauded", JSON.stringify([...next]));
    try {
      const res = await api.post(`/fieldnotes/${noteId}/applaud`);
      setNotes((prev) =>
        prev.map((n) => (n.id === noteId ? { ...n, applause: res.data.applause } : n))
      );
    } catch {
      // lokale Markierung bleibt bestehen
    }
  };

  return (
    <div data-testid="journal-page">
      <section className="mx-auto max-w-7xl px-6 pb-16 pt-28 md:px-12 md:pt-36 lg:px-16">
        <Reveal>
          <p className="mb-4 font-mono text-sm uppercase tracking-[0.15em] text-smoke">{t("Notizen von unterwegs")}</p>
        </Reveal>
        <h1 data-testid="journal-title" className="font-serif font-light leading-[0.95] tracking-tight text-bone">
          <MaskedLine delay={0.1} className="text-[clamp(3rem,9vw,7rem)]">
            Fieldnotes.
          </MaskedLine>
        </h1>
        <Reveal delay={0.2}>
          <p className="mt-8 max-w-xl text-base font-light leading-relaxed text-smoke md:text-lg" data-testid="journal-intro">
            {t("Kleine Geschichten hinter den Bildern. Von Aufträgen, von unterwegs und vom Familientisch.")}
          </p>
        </Reveal>

        <div className="mt-16 md:mt-20">
          {loading ? (
            <div className="space-y-12">
              {[...Array(3)].map((_, i) => (
                <div key={i} className="space-y-4">
                  <div className="h-3 w-48 animate-pulse bg-panel" />
                  <div className="h-5 w-2/3 animate-pulse bg-panel" />
                </div>
              ))}
            </div>
          ) : error ? (
            <p className="font-mono text-sm font-light text-smoke" data-testid="journal-error">
              {t("Inhalte konnten nicht geladen werden.")}{" "}
              <button
                type="button"
                onClick={() => load(0)}
                data-testid="journal-retry"
                className="underline decoration-ember/60 underline-offset-4 transition-colors hover:text-ember-deep"
              >
                {t("Erneut versuchen")}
              </button>
            </p>
          ) : notes.length === 0 ? (
            <p className="font-mono text-sm font-light text-smoke" data-testid="journal-empty">
              {t("Noch keine Notizen. Die erste kommt bald.")}
            </p>
          ) : (
            notes.map((note) => {
              const noteText = lang === "en" && note.text_en ? note.text_en : note.text;
              const noteLocation = lang === "en" && note.location_en ? note.location_en : note.location;
              const scatter = scatterFor(note.id);
              const isQuote = !(note.images?.length > 0);
              return (
              <article
                key={note.id}
                data-testid={`journal-note-${note.id}`}
                className={`border-t border-black/10 py-10 first:border-t-0 first:pt-0 md:py-14 ${isQuote ? "text-center" : ""}`}
              >
                <Reveal>
                  <p className="mb-5 font-mono text-[10px] uppercase tracking-[0.3em] text-smoke">
                    {formatNoteDate(note.date, lang)}
                    {noteLocation && (
                      <>
                        {" "}
                        <span className="text-ember">·</span>{" "}
                        <span className="text-ember">{noteLocation}</span>
                      </>
                    )}
                  </p>
                  {isQuote ? (
                    <div data-testid={`journal-quote-${note.id}`}>
                      <span className="mx-auto mb-8 block h-px w-12 bg-ember/70" />
                      <p className="mx-auto max-w-2xl whitespace-pre-line font-typewriter text-xl leading-loose text-bone md:text-2xl">
                        {noteText}
                      </p>
                    </div>
                  ) : (
                    <>
                      <div className="flex flex-wrap items-center gap-x-5 gap-y-8 md:gap-x-0">
                      {note.images.map((img, i) => {
                        const sc = scatter[i % scatter.length];
                        return (
                        <span
                          key={img.image}
                          className="relative"
                          style={
                            isDesktop
                              ? {
                                  transform: `rotate(${sc.rotate}deg)`,
                                  zIndex: i,
                                  marginLeft: i > 0 ? `clamp(-${sc.overlap}px, -6vw, -20px)` : 0,
                                  marginTop: i > 0 ? sc.shiftY : 0,
                                }
                              : { zIndex: i }
                          }
                        >
                          <span className="block bg-white p-2 md:p-2.5">
                            <img
                              src={thumbUrl(img.image, 1200)}
                              alt={(lang === "en" ? note.alt_en || note.alt_de : note.alt_de) || `${noteLocation ? `${noteLocation}, ` : ""}${lang === "en" ? "Fieldnote by" : "Fieldnote von"} Wim Jansen Delgado`}
                              loading="lazy"
                              decoding="async"
                              className={`block w-auto ${
                                i === 0 ? "h-72 md:h-96" : "h-64 md:h-80"
                              }`}
                            />
                          </span>
                        </span>
                        );
                      })}
                      </div>
                      <p className="mt-10 max-w-2xl whitespace-pre-line font-typewriter text-sm leading-loose text-bone md:text-base">
                        {noteText}
                      </p>
                    </>
                  )}
                  <button
                    type="button"
                    onClick={() => applaud(note.id)}
                    data-testid={`applaud-btn-${note.id}`}
                    aria-pressed={applauded.has(note.id)}
                    className="group mt-8 inline-flex items-center gap-3 font-mono text-[10px] uppercase tracking-[0.3em] text-smoke transition-colors duration-300 hover:text-ember-deep"
                  >
                    <span
                      className={`h-2 w-2 rounded-full border transition-all duration-300 ${
                        applauded.has(note.id)
                          ? "border-ember-deep bg-ember-deep"
                          : "border-smoke/50 group-hover:scale-125 group-hover:border-ember"
                      }`}
                    />
                    {applauded.has(note.id) ? t("Danke.") : t("Berührt mich")}
                  </button>
                </Reveal>
              </article>
              );
            }))}
        </div>
      </section>
    </div>
  );
}
